/**
 * Uber Trash transparency dashboard — usable by both Admin and PM.
 * Shows: tonight's job-close, residents who placed trash on time, residents who didn't,
 * and same-day open complaints — all on a single screen.
 */
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedFilter } from './UnifiedGeoRoleFilter';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Eye, CheckCircle2, XCircle, AlertTriangle, Clock, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UberTrashDashboard({ boardFilter = null }) {
  const { user } = useAuth();
  // Iter-104 · Item 8 — Valet CC consumes the unified filter (state, city,
  // view_as, snapshot period). Property list re-filters immediately when
  // the operator changes any of those fields.
  // Iter-112 — consumes the lifted Enterprise Pivot Filter when provided;
  // falls back to the legacy shim (no-op) for standalone mounts.
  // 'corporate' aggregates both lines, so valet data stays visible; only
  // an exclusive 'cleaners' selection hides the valet ops board.
  const shim = useUnifiedFilter();
  const filterSrc = boardFilter || shim;
  const { state: filterState, view_as, refreshTick, windowDays } = filterSrc;
  const filterCities = filterSrc.cities || (filterSrc.city ? [filterSrc.city] : []);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadProps = async () => {
    try {
      const res = await fetch(`${API}/api/properties`, { credentials: 'include' });
      if (res.ok) {
        let list = await res.json();
        // PMs only see their own properties
        if (user?.role === 'property_manager') {
          list = list.filter((p) => p.manager_id === user.user_id);
        }
        setProperties(list);
      }
    } catch (_) { /* noop */ }
  };

  // Filter properties by unified-filter scope + view_as. When view_as
  // flips to cleaners, the Valet CC surface hides — the operator should
  // consult the Cleaners CC instead. Kept as a live empty state so the
  // shell renders instantly.
  const scopedProperties = useMemo(() => {
    if (view_as === 'cleaners') return [];
    return (properties || []).filter((p) => {
      if (filterState && p.state && p.state !== filterState) return false;
      if (filterCities.length && p.city && !filterCities.includes(p.city)) return false;
      return true;
    });
  }, [properties, filterState, filterCities, view_as]);

  useEffect(() => {
    if (!scopedProperties.length) { setPropertyId(''); return; }
    if (!scopedProperties.find((p) => p.property_id === propertyId)) {
      setPropertyId(scopedProperties[0].property_id);
    }
  }, [scopedProperties, propertyId]);

  const loadFeed = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/ops/uber-trash/property/${propertyId}?service_date=${serviceDate}`,
        { credentials: 'include' }
      );
      if (res.ok) setFeed(await res.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { loadProps(); }, [user?.user_id, user?.role, refreshTick]);
  useEffect(() => { loadFeed(); }, [propertyId, serviceDate, refreshTick, windowDays]);

  return (
    <Card className="bg-zinc-900 border-2 border-[#FFD60A] p-6" data-testid="uber-trash-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#FFD60A] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Eye className="h-6 w-6" /> UBER TRASH — TRANSPARENCY FEED
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Live view of who placed trash on time, when the crew closed the job, and any open complaints — all in one place.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-56" data-testid="uber-trash-property-select">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {scopedProperties.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white w-40"
            data-testid="uber-trash-date"
          />
          <Button onClick={loadFeed} variant="outline" className="border-[#FFD60A] text-[#FFD60A]" disabled={loading} data-testid="uber-trash-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!feed ? (
        <p className="text-sm text-zinc-500 text-center py-8">{loading ? 'Loading...' : 'Pick a property to view the feed.'}</p>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Tile color="bg-zinc-800" label="Total units" value={feed.totals.total_units} testid="uber-tile-total" />
            <Tile color="bg-emerald-700" label="On time" value={feed.totals.on_time} testid="uber-tile-on-time" />
            <Tile color="bg-amber-600" label="Late" value={feed.totals.late} testid="uber-tile-late" />
            <Tile color="bg-blue-700" label="Picked up" value={feed.totals.picked_up} testid="uber-tile-picked" />
            <Tile color="bg-red-700" label="Didn't place" value={feed.totals.not_placed} testid="uber-tile-not-placed" />
          </div>

          {/* Job close banner */}
          <div className="mb-5">
            {feed.job_close ? (
              <div className="flex items-center justify-between bg-emerald-900/40 border border-emerald-700 rounded-lg p-4" data-testid="uber-job-close-banner">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <div>
                    <div className="text-sm font-bold text-emerald-300">Job closed</div>
                    <div className="text-xs text-emerald-200">
                      Closed by {feed.job_close.actor_name} {feed.job_close.actor_badge ? `(${feed.job_close.actor_badge})` : ''} at {new Date(feed.job_close.closed_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                {feed.job_close.notes && <div className="text-xs text-emerald-200 italic">"{feed.job_close.notes}"</div>}
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-amber-900/30 border border-amber-700 rounded-lg p-4" data-testid="uber-job-not-closed">
                <Clock className="h-5 w-5 text-amber-400" />
                <span className="text-sm text-amber-200">Job not yet closed for this date.</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Placed list */}
            <Card className="bg-zinc-800 border-zinc-700 p-4">
              <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Residents who placed trash
              </h3>
              {feed.placements.length === 0 ? (
                <p className="text-xs text-zinc-500">None yet.</p>
              ) : (
                <ul className="space-y-1 max-h-72 overflow-y-auto">
                  {feed.placements.map((p) => (
                    <li key={p.placement_id} className="flex items-center justify-between text-xs text-zinc-200" data-testid={`uber-placed-${p.placement_id}`}>
                      <span>Unit {p.unit_number} · {p.tenant_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">{new Date(p.placed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <Badge className={p.placement_status === 'on_time' ? 'bg-emerald-600' : 'bg-amber-600 text-black'}>
                          {p.placement_status === 'on_time' ? 'on time' : 'late'}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Not placed list */}
            <Card className="bg-zinc-800 border-zinc-700 p-4">
              <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-400" /> Did NOT place trash
              </h3>
              {feed.not_placed.length === 0 ? (
                <p className="text-xs text-zinc-500">All accounted for.</p>
              ) : (
                <ul className="space-y-1 max-h-72 overflow-y-auto">
                  {feed.not_placed.map((t) => (
                    <li key={t.user_id} className="flex items-center justify-between text-xs text-zinc-200" data-testid={`uber-not-placed-${t.user_id}`}>
                      <span>Unit {t.unit_number} · {t.name}</span>
                      <Badge className="bg-red-700">no check-in</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* Open complaints cross-reference */}
          {feed.open_complaints?.length > 0 && (
            <Card className="bg-zinc-800 border-amber-700/40 p-4 mt-4">
              <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" /> Open complaints to cross-check
              </h3>
              <ul className="space-y-1 max-h-60 overflow-y-auto">
                {feed.open_complaints.map((v) => {
                  const placedByUnit = feed.placements.find((p) => p.unit_number === v.unit_number);
                  return (
                    <li key={v.violation_id} className="text-xs text-zinc-200 flex items-start gap-2 py-1" data-testid={`uber-complaint-${v.violation_id}`}>
                      <Badge className="bg-amber-500 text-black mt-0.5">{v.violation_type}</Badge>
                      <div className="flex-1">
                        <div>Unit {v.unit_number} · {v.description}</div>
                        <div className="text-[10px] text-zinc-500">
                          {placedByUnit
                            ? `Resident DID place at ${new Date(placedByUnit.placed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${placedByUnit.placement_status})`
                            : 'Resident did NOT place trash on this date'}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </>
      )}
    </Card>
  );
}

function Tile({ color, label, value, testid }) {
  return (
    <div className={`${color} rounded-lg p-3 text-white`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] opacity-80 uppercase tracking-wide">{label}</div>
    </div>
  );
}
